import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GoogleMapReact from 'google-map-react';
import { abbreviateNumber, convertCurrency, mapOptions } from 'src/utils/functions';
import { GOOGLE_MAPS_API_KEY } from 'src/keys';
import { Pointer } from 'src/App/HOC';
import { convertData, getIfReportTypeDisabled } from '../../../helper';
import CenterPointer from 'src/App/HOC/Pointer/CenterPointer';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';

import { reportPageStyles as styles } from '../styles';
import CloseIcon from '@mui/icons-material/Close';
import { COLOR_RED, exchangeRates } from 'src/const';
import { COLOR_ERROR_1, COLOR_SECONDARY_2_600, COLOR_SECONDARY_2_800 } from 'src/colors';

import TooltipNew from '../../../../../HOC/Tooltips/TooltipNew';
import { UserReportAccessTypes } from '../../../propTypes';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SimplifiedAirbnbPointerBody from 'src/App/HOC/Cards/SimplifiedAirbnbPointerBody';
import { IndividualListingModalV2 } from 'src/App/Modals';
import ToggleVisibilityButton from 'src/App/Components/Buttons/ToggleVisibilityButton';
import { MapListingCountInfoBox } from 'src/App/HOC/Alerts/MapListingCountInfoBoxAlert';
import { SubscriptionModalOrigin, SubscriptionPlan } from 'src/interfaces/idashboard';
import ListingCardErrorBoundary from 'src/App/Components/ErrorBoundaries/ListingCardErrorBoundary';

const sortSelectedCompsFirst = (comps: any) => {
  const selectedComps = comps
    .filter((comp: any) => comp.is_selected == 1)
    .map((comp: any) => comp.listingID);
  const unselectedComps = comps
    .filter((comp: any) => comp.is_selected == 0)
    .map((comp: any) => comp.listingID);
  return [...selectedComps, ...unselectedComps];
};

const getSortedCompsWindow = (
  comps: any,
  sortedComps: string[],
  startIndex: number,
  endIndex: number
) => {
  return sortedComps
    .slice(startIndex, endIndex)
    .map((compId: string) => comps.find((comp: any) => comp.listingID == compId))
    .sort((a: any, b: any) => (a.rank ?? 0) - (b.rank ?? 0));
};

const CompsEditModal = ({
  reportId,
  comps,
  currency,
  isCompsEditModalOpen,
  setIsCompsEditModalOpen,
  handleReport,
  centerPin,
  trackEditPerformed,
  reportAccessType,
  handleLoginOrPricingModalOpen,
  mapCenter,
  radius,
  referenceADR,
  handleOnSampleClick,
}: {
  reportId: string;
  comps: any;
  currency: string;
  isCompsEditModalOpen: boolean;
  setIsCompsEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleReport: (method: any, requestBody: null | any, id?: string) => Promise<void>;
  centerPin: () => JSX.Element;
  trackEditPerformed: (editType: any) => void;
  reportAccessType: UserReportAccessTypes;
  handleLoginOrPricingModalOpen: (
    state: 'open' | 'close',
    forceOpen?: 'payment' | null,
    estimatorSimplePricingModalViewMode?: 'expert' | 'scale',
    triggerPaymentInterestAllowed?: boolean
  ) => void;
  mapCenter: { lat: number; lng: number };
  radius: number;
  referenceADR: number;
  handleOnSampleClick: (
    reqSubscriptionPlan?: SubscriptionPlan,
    origin?: SubscriptionModalOrigin | null
  ) => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isFetchMoreCompsButtonDisabled: boolean = getIfReportTypeDisabled(
    reportAccessType,
    'CompsFilterFetchMoreListingsButton'
  );

  const compsDisplayOrder = sortSelectedCompsFirst(comps);

  const [selectedComps, setSelectedComps] = useState<{ [key: string]: boolean }>(
    comps.reduce((acc: { [key: string]: boolean }, comp: any) => {
      acc[comp.listingID] = comp.is_selected == 1 ? true : false;
      return acc;
    }, {})
  );
  const [visibleCompsView, setVisibleCompsView] = useState<Set<string>>(new Set<string>());

  const [currentCompsSelectionWindow, setCurrentCompsSelectionWindow] = useState<[number, number]>([
    0,
    10,
  ]);

  const handleSetCompsSelectionWindow = () => {
    let viewWindowRange: [number, number] = [0, 0];
    setCurrentCompsSelectionWindow(prev => {
      const newStart = prev[0];
      const newEnd = Math.min(40, (prev[1] / 10 + 1) * 10);
      viewWindowRange = [newStart, newEnd];
      return [newStart, newEnd];
    });

    const newVisibleListingSet = new Set<string>();
    getSortedCompsWindow(
      parsedComps,
      compsDisplayOrder,
      viewWindowRange[0],
      viewWindowRange[1]
    ).map(comp => {
      newVisibleListingSet.add(comp.listingID);
    });
    setVisibleCompsView(newVisibleListingSet);
  };

  const handleCloseEditModal = () => {
    setIsCompsEditModalOpen(false);
  };

  const handleCompsUpdate = () => {
    const parsedSelectedComps = Object.keys(selectedComps).reduce(
      (acc: { [key: string]: number }, compId: string) => {
        acc[compId] = selectedComps[compId] ? 1 : 0;
        return acc;
      },
      {}
    );

    const updateCompsObject = {
      update_type: 'comps',
      id: reportId,
      comps: parsedSelectedComps,
    };

    handleReport('UPDATE', updateCompsObject);
  };

  const totalNumberOfCompsSelected: number = Object.keys(selectedComps).filter(
    (compId: string) => selectedComps[compId]
  ).length;

  const parsedComps = comps
    .map((comp: any) => convertData(comp, currency))
    .sort((a: any, b: any) => (a.rank ?? 0) - (b.rank ?? 0));

  const isHistoricalDataHidden = getIfReportTypeDisabled(
    reportAccessType,
    'ListingMarkerCardLTMHistoryVisibility'
  );

  const [selectedCompsView, setSelectedCompsView] = useState<any>(null);
  const [isSelectedCompDisabled, setIsSelectedCompDisabled] = useState<boolean>(false);

  const [currentListingHovered, setCurrentListingHovered] = useState<string>('');

  const rearrangeCompsToSelectForMap = parsedComps => {
    const notSelectedList: any[] = [];
    const selectedList: any[] = [];
    let hoveredOne: any = null;

    parsedComps.forEach(comp => {
      if (comp.listingID === currentListingHovered) {
        hoveredOne = comp;
      } else if (selectedComps[comp.listingID]) {
        selectedList.push(comp);
      } else {
        notSelectedList.push(comp);
      }
    });

    let arrayToReturn = hoveredOne ? [hoveredOne] : [];
    arrayToReturn = [...notSelectedList, ...selectedList, ...arrayToReturn];

    return arrayToReturn;
  };

  const getBoundsFromRadius = (map, maps, center, radius) => {
    const bounds = new maps.LatLngBounds();

    // Dynamically adjust radius for the mobile screen
    const adjustedRadius = window.innerWidth < 900 ? radius * 0.4 : radius;

    // Calculate points at 0, 90, 180, and 270 degrees based on the adjusted radius
    const northPoint = maps.geometry.spherical.computeOffset(center, adjustedRadius, 0); // North
    const southPoint = maps.geometry.spherical.computeOffset(center, adjustedRadius, 180); // South
    const eastPoint = maps.geometry.spherical.computeOffset(center, adjustedRadius, 90); // East
    const westPoint = maps.geometry.spherical.computeOffset(center, adjustedRadius, 270); // West

    bounds.extend(northPoint);
    bounds.extend(southPoint);
    bounds.extend(eastPoint);
    bounds.extend(westPoint);

    map.fitBounds(bounds);
  };

  const handleApiLoaded = (map, maps) => {
    const googleCenter = new maps.LatLng(mapCenter.lat, mapCenter.lng);

    getBoundsFromRadius(map, maps, googleCenter, radius);
  };

  return (
    <Modal open={isCompsEditModalOpen} onClose={handleCloseEditModal}>
      <>
        <Box
          sx={{
            ...styles.filterModalBodyRoot,
            width: `clamp(300px, '90vw', 1400px)`,
            p: isMobile ? 0 : 4,
            ...(isMobile && {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxHeight: '100vh',
              borderRadius: 0,
            }),
            ...(!isMobile && { paddingTop: 0, paddingLeft: 0 }),
          }}
        >
          <IconButton
            onClick={handleCloseEditModal}
            sx={{
              ...styles.filterModalCloseIcon,
              zIndex: 9999,
              background: '#fff',
              color: COLOR_RED,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Grid container xs={12} justifyContent={'space-between'} sx={{ mt: '0px' }}>
            {/* LEFT - MINI MAP */}
            <Grid
              container
              item
              xs={isMobile ? 12 : 3}
              sx={{
                height: isMobile ? '200px' : 'calc(80vh + 30px)',
                position: isMobile ? 'relative' : 'sticky',
                top: 0,
                left: 0,
                ...(isMobile && {
                  width: 'clamp(300px, 100vw, 700px)',
                  padding: '0 !important',
                  borderBottomLeftRadius: '20px',
                  borderBottomRightRadius: '20px',
                  overflow: 'hidden',
                }),
              }}
            >
              <GoogleMapReact
                bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
                center={{ lat: mapCenter.lat, lng: mapCenter.lng }}
                defaultZoom={12}
                options={map => mapOptions(map, true, 'RIGHT_BOTTOM')}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
              >
                {centerPin()}
                {rearrangeCompsToSelectForMap(parsedComps).map((comp, index) => {
                  const isCurrentListingDisabled = getIfReportTypeDisabled(
                    reportAccessType,
                    'ListingMarker',
                    index
                  );

                  return (
                    <CenterPointer
                      key={'map-comps-selection-listing-' + index}
                      lat={comp.latitude}
                      lng={comp.longitude}
                      listingId={comp.listingID}
                      revenueLtm={
                        isCurrentListingDisabled
                          ? undefined
                          : comp.annualRevenueLTM < 1
                          ? 'Inactive'
                          : exchangeRates[currency].symbol +
                            abbreviateNumber(convertCurrency(currency, comp.annualRevenueLTM))
                      }
                      isSelected={
                        selectedComps[comp.listingID] || visibleCompsView.has(comp.listingID)
                      }
                      setCurrentListingHovered={setCurrentListingHovered}
                      isHovered={comp.listingID === currentListingHovered}
                      isMobileView={isMobile}
                    />
                  );
                })}
              </GoogleMapReact>
            </Grid>
            {/* RIGHT COMPS */}
            <Grid
              container
              item
              xs={isMobile ? 12 : 9}
              sx={{
                background: '#fff',
                ...(isMobile && {
                  p: '30px',
                  height: 'calc(100vh - 200px)',
                  overflowY: 'auto',
                }),
                ...(!isMobile && {
                  paddingTop: isMobile ? '10px' : '20px',
                  paddingLeft: isMobile ? '10px' : '20px',
                }),
              }}
            >
              <Grid item xs={0} sx={{ mb: '10px' }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, marginBottom: isMobile ? '10px' : '20px' }}
                >
                  Edit Comparables
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                  In this tab, you can add or remove listings from the report. Only selected
                  listings will be used to estimate the revenue for your address.
                </Typography>
              </Grid>
              <Grid container spacing={2} item xs={12}>
                {getSortedCompsWindow(
                  parsedComps,
                  compsDisplayOrder,
                  currentCompsSelectionWindow[0],
                  currentCompsSelectionWindow[1]
                ).map((comp, index) => {
                  const isCurrentListingDisabled = getIfReportTypeDisabled(
                    reportAccessType,
                    'ListingMarker',
                    index
                  );

                  const listingId = comp.listingID;

                  const handleVisibilityClick = () => {
                    setSelectedComps({
                      ...selectedComps,
                      [listingId]: !selectedComps[listingId],
                    });
                  };

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      id={'card-' + listingId}
                      onMouseEnter={() => {
                        setCurrentListingHovered(listingId);
                      }}
                      onClick={() => setCurrentListingHovered(listingId)}
                    >
                      <ListingCardErrorBoundary>
                        <SimplifiedAirbnbPointerBody
                          key={'edit-comps' + index}
                          isDisabled={isCurrentListingDisabled}
                          isEstimator={false}
                          data={comp}
                          currency={currency}
                          canDelete={false}
                          deleteListing={() => {}}
                          setOpenFullView={() => {
                            setSelectedCompsView(comp);
                            setIsSelectedCompDisabled(isCurrentListingDisabled);
                          }}
                          estimatorSampleListingData={undefined}
                          isHidden={false}
                          handleOnSampleClick={handleOnSampleClick}
                          disabledViewMoreButtonAction={
                            isCurrentListingDisabled
                              ? () => {
                                  handleLoginOrPricingModalOpen('open', 'payment', 'expert');
                                }
                              : undefined
                          }
                          isCompsSelectedFromCalculatorCompsSelection={selectedComps[listingId]}
                          handleCompsAdditionCalculatorCompsSection={handleVisibilityClick}
                          showDesktopUi={true}
                          referenceADR={referenceADR}
                        />
                      </ListingCardErrorBoundary>
                    </Grid>
                  );
                })}
                <Grid
                  container
                  xs={12}
                  justifyContent={'flex-end'}
                  sx={{ mt: '20px', gap: '10px' }}
                >
                  {currentCompsSelectionWindow[1] < 40 && (
                    <TooltipNew
                      arrow={false}
                      placement="bottom"
                      title={
                        'Available in the paid plan only. Add more listings to your report for enhanced insights.'
                      }
                      content={''}
                      disable={isFetchMoreCompsButtonDisabled}
                    >
                      <Button
                        variant="outlined"
                        onClick={() =>
                          !isFetchMoreCompsButtonDisabled && handleSetCompsSelectionWindow()
                        }
                        startIcon={<AddRoundedIcon />}
                        sx={{
                          borderWidth: '2px',
                        }}
                      >
                        Add More Comps
                      </Button>
                    </TooltipNew>
                  )}
                  <TooltipNew
                    arrow={false}
                    placement="left"
                    title={'Please select at least 1 comps.'}
                    content={''}
                    disable={totalNumberOfCompsSelected < 10}
                  >
                    <Button
                      onClick={() => {
                        trackEditPerformed('map');
                        totalNumberOfCompsSelected > 0 && handleCompsUpdate();
                      }}
                      sx={{
                        ...styles.actionSectionButton,
                        background: COLOR_SECONDARY_2_800,
                        '&:hover': {
                          background: COLOR_SECONDARY_2_600,
                        },
                      }}
                    >
                      Apply
                    </Button>
                  </TooltipNew>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {selectedCompsView && (
          <IndividualListingModalV2
            open={true}
            onClose={() => setSelectedCompsView(null)}
            data={{
              ...selectedCompsView,
            }}
            listings={parsedComps}
            currency={currency}
            isDisabled={isSelectedCompDisabled !== null ? isSelectedCompDisabled : false}
            setSubscriptionModalOpen={() => {}}
            subscriptionModalOpen={false}
            multipleBookingMarkersCallLoading={false}
            isEstimator={true}
            openDemoBookingForm={() => {}}
            handleOnSampleClick={handleOnSampleClick}
            setDemoFormHeadingFor={undefined}
            isHistoricalDataHidden={isHistoricalDataHidden}
            onEstimatorLockedMarkerHistoricalDataClick={() => {
              isHistoricalDataHidden && handleLoginOrPricingModalOpen('open', 'payment', 'expert');
            }}
            referenceADR={referenceADR}
          />
        )}
      </>
    </Modal>
  );
};

const ReportPageMapSection = ({
  reportId,
  lat,
  lng,
  radius,
  comps,
  currency,
  handleReport,
  isUserReportOwner,
  trackEditOpened,
  trackEditPerformed,
  isOnboardingComplete,
  isUserSubscribed,
  handleLoginOrPricingModalOpen,
  reportAccessType,
  kpiData,
  compsStatus,
  handleOnSampleClick,
}: {
  reportId: string;
  lat: number;
  lng: number;
  radius: number;
  comps: any;
  currency: string;
  handleReport: (method: any, requestBody: null | any, id?: string) => Promise<void>;
  isUserReportOwner: boolean;
  trackEditOpened: (editType: any) => void;
  trackEditPerformed: (editType: any) => void;
  isOnboardingComplete: boolean;
  isUserSubscribed: boolean;
  handleLoginOrPricingModalOpen: (
    state: 'open' | 'close',
    forceOpen?: 'payment' | null,
    estimatorSimplePricingModalViewMode?: 'expert' | 'scale',
    triggerPaymentInterestAllowed?: boolean
  ) => void;
  reportAccessType: UserReportAccessTypes;
  kpiData: any;
  compsStatus: 'fetching' | 'success' | 'failed';
  handleOnSampleClick: (
    reqSubscriptionPlan?: SubscriptionPlan,
    origin?: SubscriptionModalOrigin | null
  ) => void;
}) => {
  const [mapRef, setMapRef] = useState<{
    map: any;
    maps: any;
  } | null>(null);
  const [searchedRegionBoundry, setSearchedRegionBoundry] = useState<any>(null);
  const [isCompsEditModalOpen, setIsCompsEditModalOpen] = useState<boolean>(false);

  const referenceADR = kpiData?.edited?.ltm_nightly_rate
    ? kpiData?.edited?.ltm_nightly_rate
    : kpiData?.[50]?.ltm_nightly_rate;

  const ExpandButton = () => {
    return (
      <Box
        onClick={() => setIsCompsEditModalOpen(true)}
        sx={{
          width: '40px',
          height: '40px',
          background: '#fff',
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
          position: 'absolute',
          right: '30px',
          bottom: '140px',
          zIndex: 100,
          cursor: 'pointer',
          transition: 'all 0.25s ease',
          '&:hover': {
            background: '#222',
            color: '#fff',
          },
        }}
      >
        <OpenInFullIcon />
      </Box>
    );
  };

  const apiIsLoaded = (map, maps) => {
    setMapRef({ map, maps });
    makeCircle(map, maps);
  };

  const makeCircle = (map, maps) => {
    if (!maps || !radius) {
      return;
    }

    const highlightedEstimatorRegion = new maps.Circle({
      strokeColor: '#52C879',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#4aa59a',
      fillOpacity: 0.12,
      center: { lat, lng },
      radius: radius,
    });
    highlightedEstimatorRegion.setMap(map);

    setSearchedRegionBoundry(highlightedEstimatorRegion);

    map.fitBounds(highlightedEstimatorRegion.getBounds());
  };

  const markers = useMemo(() => {
    if (comps) {
      const parsedComps = comps
        .filter(item => item.is_selected == 1)
        .map((item, index: number) => convertData(item, currency));

      return parsedComps
        .map((item, index: number) => {
          const isCurrentListingDisabled = getIfReportTypeDisabled(
            reportAccessType,
            'ListingMarker',
            index
          );
          const isHistoricalDataHidden = getIfReportTypeDisabled(
            reportAccessType,
            'ListingMarkerCardLTMHistoryVisibility'
          );

          return (
            <Pointer
              key={index + '-estimator-map-pointer'}
              lat={item.latitude}
              lng={item.longitude}
              data={item}
              setSubscriptionModalOpen={() => {}}
              listings={parsedComps}
              isDisabled={isCurrentListingDisabled}
              currency={currency}
              canDelete={true}
              subscriptionModalOpen={false}
              multipleBookingMarkersCallLoading={false}
              showRevenuePotential={true}
              isEstimator={true}
              deletedEstimatorPointer={false}
              handleOnSampleClick={handleOnSampleClick}
              hiddenListingIds={[]}
              addHiddenListingId={() => {}}
              removeHiddenListingId={() => {}}
              isEstimatorLocked={false}
              isHistoricalDataHidden={isHistoricalDataHidden}
              onEstimatorLockedMarkerClick={() =>
                isUserReportOwner &&
                isCurrentListingDisabled &&
                handleLoginOrPricingModalOpen('open', 'payment', 'expert')
              }
              onEstimatorLockedMarkerHistoricalDataClick={() =>
                isUserReportOwner &&
                isHistoricalDataHidden &&
                handleLoginOrPricingModalOpen('open', 'payment', 'expert')
              }
              referenceADR={referenceADR}
            />
          );
        })
        .reverse();
    }
    return;
  }, []);

  const centerPin = () => {
    return <CenterPointer lat={lat} lng={lng} CustomIcon={PushPinRoundedIcon} />;
  };

  return (
    <>
      <Grid
        item
        container
        id="estimator-map-container"
        alignItems="flex-start"
        justifyContent="center"
        sx={{ ...styles.paper, position: 'relative', paddingTop: '60px', height: '430px' }}
      >
        {compsStatus == 'fetching' && (
          <Box
            sx={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              background: '#0008',
              padding: '5px 10px',
              backdropFilter: 'blur(4px)',
              overflow: 'hidden',
              height: 'auto',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" style={{ color: '#fff' }}>
              Fetching comps... Please wait while we gather the latest data{' '}
              <CircularProgress size={14} sx={{ color: 'white' }} />
            </Typography>
          </Box>
        )}
        {compsStatus == 'failed' && (
          <Box
            sx={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              background: COLOR_ERROR_1,
              padding: '5px 10px',
              backdropFilter: 'blur(4px)',
              overflow: 'hidden',
              height: 'auto',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" style={{ color: '#fff' }}>
              Failed to fetch comps. Please recreate the report.
            </Typography>
          </Box>
        )}
        <Box sx={{ position: 'absolute', top: '10px', right: '10px' }}>
          <ToggleVisibilityButton
            title="Add or Remove Comps"
            isDisabled={!isUserReportOwner || compsStatus == 'fetching'}
            actionOnClick={() => {
              setIsCompsEditModalOpen(true);
              trackEditOpened('map');
            }}
            isLocked={false}
            Icon={ModeEditOutlineOutlinedIcon}
          />
        </Box>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: GOOGLE_MAPS_API_KEY,
            libraries: ['drawing', 'places', 'visualization'],
            id: 'estimator-map',
          }}
          center={{ lat, lng }}
          options={map => ({
            ...mapOptions,
            zoomControlOptions: {
              position: map.ControlPosition.RIGHT_BOTTOM,
            },
            fullscreenControl: false,
          })}
          zoom={12}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps)}
        >
          {centerPin()}
          {markers}
        </GoogleMapReact>
        {!isCompsEditModalOpen && ExpandButton()}
      </Grid>
      <CompsEditModal
        reportId={reportId}
        comps={comps}
        currency={currency}
        isCompsEditModalOpen={isCompsEditModalOpen}
        setIsCompsEditModalOpen={setIsCompsEditModalOpen}
        handleReport={handleReport}
        centerPin={centerPin}
        trackEditPerformed={trackEditPerformed}
        reportAccessType={reportAccessType}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        mapCenter={{ lat, lng }}
        radius={radius}
        referenceADR={referenceADR}
        handleOnSampleClick={handleOnSampleClick}
      />
    </>
  );
};

export default ReportPageMapSection;
